import React from "react";
import {
  HeartHandshake,
  EyeOff,
  WifiOff,
  Church,
  Home,
  Smartphone,
  UserRound,
  HandHeart,
  CalendarDays,
  Mic2,
  Droplets,
  Package,
  ClipboardCheck,
  Sparkles,
  NotebookPen,
  BarChart2,
  Megaphone,
  Trophy,
  UsersRound,
  BookText,
  MessageSquare,
  Wallet,
} from "lucide-react";
import TopBar from "../components/TopBar";
import BrandLogo from "../components/shared/BrandLogo";

const version = process.env.REACT_APP_VERSION;

const PHILOSOPHY = [
  {
    icon: HeartHandshake,
    title: "Cuidado, no control",
    body: "No está diseñada para medir ni auditar. Está diseñada para recordar — para que el pastor llegue a una visita y ya sepa el nombre, la historia, lo que esa familia está viviendo.",
  },
  {
    icon: EyeOff,
    title: "Tecnología invisible",
    body: "La app ideal es aquella en la que el pastor ni siquiera piensa. Solo cuida a sus personas con más profundidad y menos esfuerzo administrativo.",
  },
  {
    icon: WifiOff,
    title: "Offline-first",
    body: "El ministerio ocurre donde no hay señal. Kéves funciona sin conexión porque el cuidado no puede esperar.",
  },
];

const MODULES = [
  { icon: Church,         name: "Iglesias",        desc: "Congregaciones, líderes, horarios y coordinadores" },
  { icon: Home,           name: "Familias",         desc: "El núcleo del rebaño y estado de cada hogar" },
  { icon: UserRound,      name: "Personas",         desc: "Historia individual, bautismo y seguimiento" },
  { icon: HeartHandshake, name: "Visitas",          desc: "Planifica y registra cada visita pastoral" },
  { icon: HandHeart,      name: "Oraciones",        desc: "Pedidos del rebaño para interceder con intención" },
  { icon: CalendarDays,   name: "Agenda",           desc: "Eventos y cultos del distrito en un calendario" },
  { icon: Mic2,           name: "Predicaciones",    desc: "Rol de predicaciones por iglesia y ciclo" },
  { icon: Droplets,       name: "Bautismos",        desc: "Candidatos, estudios bíblicos y registro" },
  { icon: Package,        name: "Materiales",       desc: "Distribución, pagos y flujo financiero" },
  { icon: ClipboardCheck, name: "Revisión mensual", desc: "Evaluación mensual por congregación" },
  { icon: Sparkles,       name: "Kevia IA",         desc: "Asistente pastoral con contexto doctrinal adventista" },
  { icon: NotebookPen,    name: "Notas",            desc: "Observaciones vinculadas a personas e iglesias" },
  { icon: BarChart2,      name: "Reportes",         desc: "Indicadores de salud del distrito" },
  { icon: Smartphone,     name: "Portal del miembro", desc: "Biblia, devocional, himnario y comunidad para la iglesia" },
  { icon: Megaphone,      name: "Anuncios",           desc: "Comunicados al distrito e iglesias" },
  { icon: Trophy,         name: "Reto Semanal",       desc: "Desafíos semanales para los miembros" },
  { icon: UsersRound,     name: "Grupos Pequeños",    desc: "Evangelismo y grupos de crecimiento" },
  { icon: BookText,       name: "Planificador",       desc: "Preparación y estructura de sermones" },
  { icon: MessageSquare,  name: "Mensajes",           desc: "Comunicación directa con los miembros" },
  { icon: Wallet,         name: "Gastos",             desc: "Control financiero del distrito" },
];

const VISION = [
  "Suscripción por distrito — Para sostener el desarrollo y seguir sirviendo a más pastores. Accesible, sin fines de lucro.",
  "Más idiomas — Para que Kéves pueda servir a distritos en otras lenguas. El rebaño no tiene fronteras.",
  "App nativa — Kéves en la App Store y Google Play para una experiencia aún más completa.",
];

const Divider = () => (
  <div className="border-t border-slate-100 dark:border-slate-800" />
);

const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
    {children}
  </p>
);

export function SettingsAboutContent() {
  return (
    <div className="px-5 py-8 space-y-10">

      {/* ── SECCIÓN 1: IDENTIDAD ─────────────────────────────────────────── */}
      <div className="space-y-5">

        {/* Hero gradient card */}
        <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#012e68] via-[#1b4f9c] to-[#2563eb] px-6 py-8 text-center shadow-blue dark:shadow-none">
          <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-white/[0.05] blur-2xl pointer-events-none" />

          <div className="relative z-10 flex flex-col items-center gap-4">
            <div className="h-16 w-16 rounded-[22px] bg-white/15 border border-white/20 backdrop-blur-sm flex items-center justify-center">
              <BrandLogo variant="auto" className="h-10 object-contain" />
            </div>

            <div>
              <p className="text-[24px] font-bold text-white tracking-tight leading-tight">Kéves App</p>
              {version && (
                <p className="text-[12px] text-white/55 mt-0.5">versión {version}</p>
              )}
            </div>

            <div className="space-y-1.5 pt-1">
              <p className="text-[14px] font-medium text-white/90 leading-snug italic">
                «Para ser pastor, hay que vivir como oveja.»
              </p>
              <p className="text-[11px] text-white/45 tracking-wide">
                — Joshua Reyes
              </p>
            </div>
          </div>
        </div>

        {/* Versículo devocional */}
        <div className="rounded-[20px] bg-[#1b4f9c]/5 dark:bg-[#1b4f9c]/12 border border-[#1b4f9c]/10 dark:border-[#1b4f9c]/25 px-5 py-4 space-y-2">
          <p className="text-[14px] italic text-slate-700 dark:text-slate-200 leading-relaxed font-medium">
            «El Señor es mi pastor; tengo todo lo que necesito.»
          </p>
          <p className="text-[11px] font-semibold text-[#1b4f9c] dark:text-blue-400 tracking-wide">
            Salmos 23:1 (NTV)
          </p>
        </div>

        {/* Significado del nombre */}
        <p className="text-[14px] text-slate-600 dark:text-slate-300 leading-[1.75]">
          <span className="font-semibold text-slate-800 dark:text-slate-100">Kéves</span> es la
          palabra hebrea para "cordero". Le recuerda al pastor que su lugar no es encima del
          rebaño, sino adentro — no se cuida desde la distancia, sino desde la cercanía.
        </p>
      </div>

      <Divider />

      {/* ── SECCIÓN 2: POR QUÉ EXISTE ────────────────────────────────────── */}
      <div className="space-y-4">
        <SectionLabel>Desde el corazón del rebaño</SectionLabel>

        <div className="card-soft px-5 py-5 space-y-4 text-[14px] text-slate-600 dark:text-slate-300 leading-[1.75]">
          <p>
            Esta aplicación no nació en una oficina. Nació en el campo pastoral — en las visitas de
            cada día, en los nombres que uno quiere recordar y no puede, en las familias que merecen
            más atención de la que el tiempo parece permitir.
          </p>
          <p>
            La necesidad era concreta: unificar todo en un solo lugar. Plataformas dispersas, cuadernos,
            notas en el teléfono, hojas de cálculo que nadie actualiza — todo eso fragmenta el cuidado.
            Kéves nació para reunirlo. No como software, sino como herramienta ministerial.
          </p>
          <p>
            Comenzó como un proyecto personal: aprender a programar mientras construía algo que realmente
            sirviera. Horas de código, errores, reescrituras, momentos de duda y de claridad. Sin hoja de
            ruta corporativa — solo convicción, y la gratitud de que Dios equipa para lo que el ministerio
            necesita.
          </p>
          <p>
            Hoy Kéves está en manos de pastores reales, en distritos reales. Lo que empezó como una
            oración y un proyecto personal, se ha convertido en una herramienta viva — que crece con
            cada pastor que la usa y con cada familia que es mejor cuidada gracias a ella.
          </p>
        </div>

        <div className="px-1 pt-1 space-y-0.5">
          <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-200">Joshua Reyes</p>
          <p className="text-[11px] text-slate-400 dark:text-slate-500">Licenciado en Teología</p>
          <p className="text-[11px] text-slate-400 dark:text-slate-500">Pastor y entusiasta de la tecnología</p>
        </div>

        <div className="space-y-3 pt-2">
          {PHILOSOPHY.map(({ icon: Icon, title, body }) => (
            <div key={title} className="card-soft p-4 flex items-start gap-4">
              <div className="h-9 w-9 rounded-[12px] bg-[#1b4f9c]/8 dark:bg-[#1b4f9c]/15 flex items-center justify-center shrink-0 mt-0.5">
                <Icon size={16} className="text-[#1b4f9c] dark:text-blue-400" />
              </div>
              <div className="space-y-1 flex-1 min-w-0">
                <p className="text-[12px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider leading-none">
                  {title}
                </p>
                <p className="text-[13px] text-slate-500 dark:text-slate-400 leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Divider />

      {/* ── SECCIÓN 3: QUÉ HACE LA APP ───────────────────────────────────── */}
      <div className="space-y-4">
        <SectionLabel>Qué hace Kéves</SectionLabel>

        <div className="grid grid-cols-2 gap-2.5">
          {MODULES.map(({ icon: Icon, name, desc }, i) => {
            const isLastOdd = MODULES.length % 2 !== 0 && i === MODULES.length - 1;
            return (
              <div
                key={name}
                className={`card-soft p-3.5 flex gap-3 ${
                  isLastOdd ? "col-span-2 flex-row items-center" : "flex-col"
                }`}
              >
                <div className="h-8 w-8 rounded-[10px] bg-[#1b4f9c]/8 dark:bg-[#1b4f9c]/15 flex items-center justify-center shrink-0">
                  <Icon size={15} className="text-[#1b4f9c] dark:text-blue-400" />
                </div>
                <div className={isLastOdd ? "flex-1 min-w-0" : ""}>
                  <p className="text-[12px] font-bold text-slate-800 dark:text-slate-100 leading-tight">
                    {name}
                  </p>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-snug mt-0.5">
                    {desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Divider />

      {/* ── SECCIÓN 4: LO QUE VIENE ──────────────────────────────────────── */}
      <div className="space-y-4">
        <SectionLabel>Lo que viene</SectionLabel>

        <p className="text-[14px] text-slate-600 dark:text-slate-300 leading-[1.7]">
          Kéves ya cubre los flujos pastorales esenciales. Aquí está lo que se viene:
        </p>

        <div className="space-y-3">
          {VISION.map((item, i) => (
            <div key={i} className="flex items-start gap-3.5">
              <div className="h-1.5 w-1.5 rounded-full bg-[#1b4f9c]/35 dark:bg-blue-400/40 mt-[9px] shrink-0" />
              <p className="text-[14px] text-slate-600 dark:text-slate-300 leading-relaxed">{item}</p>
            </div>
          ))}
        </div>

        <p className="text-[13px] italic text-slate-400 dark:text-slate-500 leading-relaxed pt-1">
          El rebaño merece ser conocido. No solo contado.
        </p>
      </div>

      <Divider />

      {/* ── SECCIÓN 5: CRÉDITOS ──────────────────────────────────────────── */}
      <div className="space-y-4">
        <SectionLabel>Créditos</SectionLabel>

        <div className="card-soft px-5 py-6 flex flex-col items-center text-center gap-3">
          <div className="h-12 w-12 rounded-[16px] bg-brand flex items-center justify-center">
            <BrandLogo variant="auto" className="h-8 object-contain" />
          </div>

          <div className="space-y-1">
            <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100">
              Creado por{" "}
              <span className="text-[#1b4f9c] dark:text-blue-400">Joshua Reyes</span>
            </p>
            <p className="text-[12px] text-slate-500 dark:text-slate-400">
              Licenciado en Teología · Pastor adventista
            </p>
          </div>

          <div className="w-full border-t border-slate-100 dark:border-slate-700/60 pt-3 space-y-1">
            <p className="text-[12px] font-medium text-slate-600 dark:text-slate-300">
              Tecnología al servicio del ministerio
            </p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">
              © 2024 – 2026 · Kéves App
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}

export default function SettingsAbout() {
  return (
    <div className="anim-in min-h-[100dvh] bg-page-bg pb-[calc(96px+env(safe-area-inset-bottom))] font-sans">
      <TopBar title="Acerca de Kéves" safeFallback="/settings" />
      <SettingsAboutContent />
    </div>
  );
}
